/**
 * Logikk for Culture and Leadership Profile Analysis
 * Håndterer klassifiseringer, mønstergjenkjenning og profil-scoring.
 */

export const LEADERSHIP_PROFILES = [
  { level: 1, label: 'Reactive leadership', color: 'rose', desc: 'Ledelse preget av brannslukking og manglende proaktivitet.' },
  { level: 2, label: 'Operational leadership', color: 'orange', desc: 'Fokus på oppgaver og drift, men manglende fokus på relasjon og kultur.' },
  { level: 3, label: 'Supportive leadership', color: 'indigo', desc: 'Ledere som er tilgjengelige og støttende, men mangler strategisk retning.' },
  { level: 4, label: 'Development-oriented leadership', color: 'emerald', desc: 'Aktivt fokus på vekst, læring og kontinuerlig forbedring.' },
  { level: 5, label: 'High-trust leadership', color: 'cyan', desc: 'Eksepsjonell tillit, myndiggjøring og psykologisk trygghet i alle ledd.' }
];

export const CULTURE_PROFILES = [
  { level: 1, label: 'Fragmented culture', color: 'rose', desc: 'Silo-mentalitet, lav tillit og uklare felles verdier.' },
  { level: 2, label: 'Risk-exposed culture', color: 'orange', desc: 'Kultur preget av frykt for feil og skjulte konflikter.' },
  { level: 3, label: 'Functional culture', color: 'indigo', desc: 'Stabil drift og profesjonalitet, men lav innovasjonsgrad.' },
  { level: 4, label: 'Developing culture', color: 'emerald', desc: 'Åpen kommunikasjon og begynnende fokus på psykologisk trygghet.' },
  { level: 5, label: 'Strong healthy culture', color: 'cyan', desc: 'Høy grad av inkludering, læring og gjensidig respekt.' }
];

export const getProfileType = (score, profiles) => {
  const index = Math.min(Math.floor(score / 20), 4);
  return profiles[index];
};

export const analyzeRiskPatterns = (metrics) => {
  const patterns = [];

  // Pattern 1: Leadership support gap
  if (metrics.leadership < 45 && metrics.stress > 70) {
    patterns.push({
      id: 'rp1',
      title: 'Leadership Support Gap',
      desc: 'Lav lederscore kombinert med høyt stress indikerer at ansatte står alene i krevende arbeidsoppgaver.',
      severity: 'Critical',
      icon: 'UserX'
    });
  }

  // Pattern 2: Unsafe communication climate
  if (metrics.psychSafety < 40 && metrics.conflict > 60) {
    patterns.push({
      id: 'rp2',
      title: 'Unsafe Communication Climate',
      desc: 'Lav psykologisk trygghet og høy konfliktrisiko skaper en kultur hvor problemer skjules fremfor å løses.',
      severity: 'High',
      icon: 'ShieldOff'
    });
  }

  // Pattern 3: Structural ambiguity
  if (metrics.roleClarity < 50 && metrics.stress > 65) {
    patterns.push({
      id: 'rp3',
      title: 'Structural Ambiguity',
      desc: 'Uklarhet i roller fungerer som en katalysator for unødvendig stress og ineffektivitet.',
      severity: 'Medium',
      icon: 'Layers'
    });
  }

  // Pattern 4: Weak team culture
  if (metrics.collaboration < 45 && metrics.trust < 50) {
    patterns.push({
      id: 'rp4',
      title: 'Weak Team Culture',
      desc: 'Manglende samarbeidsevne og lav tillit hindrer teamet i å fungere som en samlet enhet.',
      severity: 'High',
      icon: 'Users'
    });
  }

  return patterns;
};

export const getRecommendedActions = (patterns, leadershipScore, cultureScore) => {
  const actions = [];
  
  if (leadershipScore < 50) {
    actions.push({ title: 'Leadership Coaching', type: 'Individual', desc: 'Målrettet veiledning for å styrke støttende og tydelig ledelse.' });
    actions.push({ title: 'Manager Follow-up Plan', type: 'Strategic', desc: 'Strukturert oppfølging av operative ledere.' });
  }

  if (cultureScore < 50) {
    actions.push({ title: 'Psychological Safety Workshop', type: 'Team', desc: 'Bygg fundamentet for en åpen ytringskultur.' });
    actions.push({ title: 'Feedback Culture Workshop', type: 'Team', desc: 'Trening på konstruktive tilbakemeldings-sløyfer.' });
  }

  if (patterns.some(p => p.id === 'rp3')) {
    actions.push({ title: 'Role Clarification Workshop', type: 'Structural', desc: 'Avklaring av forventninger, ansvar og beslutningslinjer.' });
  }

  return actions;
};